import styled from 'styled-components'

import { Contract, Feature } from 'schema/contract'
import { convertPrice, formatedDate } from 'utils'

// 統合されたレスポンシブカード
const InfoCard = styled.div`
  /* PC版: グリッドレイアウト */
  @media only screen and (min-width: ${(p) => p.theme.vp + 1}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  /* SP版: カードレイアウト（最初のカードのみ） */
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 0 16px;
    margin-bottom: 16px;
    background-color: ${(p) => p.theme.BACKGROUND_SURFACE};
    border-radius: 8px;
    box-shadow: none;
    border: 1px solid ${(p) => p.theme.BORDER};

    /* SP版では2つ目のセクションを非表示 */
    > div:nth-child(2) {
      display: none;
    }
  }
`

// SP版専用の売主情報カード
const OwnerInfoCard = styled.div`
  /* PC版では非表示 */
  @media only screen and (min-width: ${(p) => p.theme.vp + 1}px) {
    display: none;
  }

  /* SP版でのみ表示 */
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 0 16px;
    margin-bottom: 16px;
    background-color: ${(p) => p.theme.BACKGROUND_SURFACE};
    border-radius: 8px;
    box-shadow: none;
    border: 1px solid ${(p) => p.theme.BORDER};
  }
`

// 統合されたセクション
const InfoSection = styled.div`
  @media only screen and (min-width: ${(p) => p.theme.vp + 1}px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`

// 統合された情報行
const InfoRow = styled.div`
  display: flex;
  align-items: center;

  padding: 6px 0;

  /* SP版 */
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    justify-content: space-between;
    height: 72px;
    padding: 0;
    border-bottom: 1px solid #e5e5e5;
  }
`

// PC版でのみ表示される要素
const PCOnlyRow = styled(InfoRow)`
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    display: none;
  }
`

// SP版でのみ表示される要素
const SPOnlyRow = styled(InfoRow)`
  @media only screen and (min-width: ${(p) => p.theme.vp + 1}px) {
    display: none;
  }
`

// 統合されたラベル
const InfoLabel = styled.div<{ size?: 'xs' | 'md' | 'lg' }>`
  font-size: ${(p) => p.theme.font.size.m};
  color: ${(p) => p.theme.FONT_COLOR};
  font-weight: 400;

  width: ${(props) => {
    switch (props.size) {
      case 'xs':
        return '80px'
      case 'md':
        return '180px'
      case 'lg':
        return '300px'
      default:
        return '180px'
    }
  }};
`

// 統合された値
const InfoValue = styled.div`
  font-size: ${(p) => p.theme.font.size.m};
  color: ${(p) => p.theme.FONT_COLOR};
  font-weight: 500;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    text-align: right;
    width: calc(100% - 80px);
  }
`

// 最後の行用のInfoRow（ボーダーなし）
const LastInfoRow = styled(InfoRow)`
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    border-bottom: none;
  }
`

// ログイン日時値（SP版でのみ改行）
const LoginDateValue = styled.div`
  text-align: right;
  font-size: 14px;
  color: #333;
  font-weight: 500;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    span {
      display: block;
    }
  }
`

// 売主情報セクションのコンポーネント
const OwnerInfoSection = ({
  contract,
  isSp = false,
}: {
  contract: Contract
  isSp?: boolean
}) => (
  <InfoSection>
    <SPOnlyRow>
      <InfoLabel size="xs">売主名</InfoLabel>
      <InfoValue>{contract.ownerName} 様</InfoValue>
    </SPOnlyRow>

    <InfoRow>
      <InfoLabel size={isSp ? 'xs' : 'md'}>売主メール</InfoLabel>
      <InfoValue>{contract.ownerMail}</InfoValue>
    </InfoRow>
    <InfoRow>
      <InfoLabel size={isSp ? 'xs' : 'md'}>売主最終ログイン日時</InfoLabel>
      <LoginDateValue>
        {contract.ownerLastLoginAt === null ? (
          '未ログイン'
        ) : (
          <>
            <span>{formatedDate(contract.ownerLastLoginAt, 'yyyy/MM/dd')}</span>
            <span>{formatedDate(contract.ownerLastLoginAt, 'HH:mm')}</span>
          </>
        )}
      </LoginDateValue>
    </InfoRow>
  </InfoSection>
)

interface ContractDetailPanelProps {
  data: Contract
  isContract: boolean
  isOwner: boolean
}

const ContractDetailPanel = ({
  data,
  isContract,
  isOwner,
}: ContractDetailPanelProps) => {
  if (!data || !isContract) {
    return <></>
  }

  const contract = data as Contract

  return (
    <>
      <InfoCard>
        {/* 基本情報セクション（PC版：左列、SP版：上部） */}
        <InfoSection>
          <InfoRow>
            <InfoLabel size="xs">物件名</InfoLabel>
            <InfoValue>{data?.name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel size="xs">物件価格</InfoLabel>
            <InfoValue>{convertPrice(data?.price)}円</InfoValue>
          </InfoRow>
          <LastInfoRow>
            <InfoLabel size="xs">契約期間</InfoLabel>
            <InfoValue>
              {formatedDate(data?.termStartAt)} -{' '}
              {formatedDate(data?.termEndAt)}
            </InfoValue>
          </LastInfoRow>
          {/* PC版でのみ表示される売主名 */}
          {!isOwner && isContract && (
            <PCOnlyRow>
              <InfoLabel size="xs">売主名</InfoLabel>
              <InfoValue>{contract.ownerName} 様</InfoValue>
            </PCOnlyRow>
          )}
        </InfoSection>

        {/* 売主情報セクション（PC版：右列、SP版：下部） */}
        {!isOwner && isContract && (
          <OwnerInfoSection contract={contract} isSp={false} />
        )}
      </InfoCard>

      {/* SP版専用の売主情報カード */}
      {!isOwner && isContract && (
        <OwnerInfoCard>
          <OwnerInfoSection contract={contract} isSp={true} />
        </OwnerInfoCard>
      )}
    </>
  )
}

interface FeatureDetailPanelProps {
  data: Feature
}

const FeatureDetailPanel = ({ data }: FeatureDetailPanelProps) => {
  if (!data) {
    return <></>
  }

  return (
    <>
      <InfoCard>
        <InfoSection>
          <InfoRow>
            <InfoLabel size="xs">物件名</InfoLabel>
            <InfoValue>{data?.name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel size="xs">価格</InfoLabel>
            <InfoValue>{convertPrice(data?.price)}円</InfoValue>
          </InfoRow>
          <LastInfoRow>
            <InfoLabel size="xs">契約期間</InfoLabel>
            <InfoValue>
              {formatedDate(data?.termStartAt)} -{' '}
              {formatedDate(data?.termEndAt)}
            </InfoValue>
          </LastInfoRow>
        </InfoSection>
      </InfoCard>
    </>
  )
}

export { ContractDetailPanel, FeatureDetailPanel }
export default ContractDetailPanel
